<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import Badge from "@/design/primitives/Badge.vue";
import Icon from "@/design/primitives/Icon.vue";
import Popover from "@/design/primitives/Popover.vue";
import { useEntities } from "@/shared/field-gaps/use-field-gaps";
import { useNotificationStream } from "../api/use-notification-stream";
import {
	dismissible,
	linkFor,
	type Notification,
	splitUnread,
	useDismiss,
	useMarkRead,
	useNotifications,
} from "../api/use-notifications";
import { useNotificationsI18n } from "../i18n/use-notifications-i18n";

/**
 * The bell.
 *
 * Where `ctx.notify` lands — a verb that used to throw because there was nowhere
 * honest to deliver to. It was the effect `notify_user` until the Fase D retired
 * the thirteen verbs that had an SDK equivalent; the door changed, the landing
 * did not. Unread counts are per person, and a notification with no destination
 * reaches everyone rather than nobody.
 *
 * ── AND WHERE IT TAKES YOU ────────────────────────────────────────────────
 *
 * A notice about a record now carries the entity code beside the id, so the line
 * is a link to the record instead of a sentence about one. Clicking marks it read
 * on the way out: having to click twice — once to read, once to go — is the kind of
 * ceremony that makes people mark everything read without looking.
 *
 * ── AND HOW THE BELL IS READ AFTER THE HUNDREDTH NOTICE ───────────────────
 *
 * Three gestures the panel did not have: filter by unread, dismiss ONE line, and
 * get past the first fifty. Without them the only gesture was "mark all as
 * read", which is what teaches people to empty the bell without reading it.
 *
 * The × only appears on the lines that are YOURS: a whole-tenant notice is ONE
 * line read by every colleague, and dismissing it would make it vanish from
 * everybody's screen. `dismissible` says which is which, and the server refuses
 * the same way.
 *
 * ── AND WHAT OPENS FIRST (29/09) ──────────────────────────────────────────
 *
 * "Não lidas devem ser prioridade ao clicar no sino e não TODAS." The server
 * lists the unread first; the panel draws a quiet "anteriores" line where the
 * read ones begin. Clicking a line ACTS (goes and marks read); the × DISMISSES
 * (reads and hides). A clicked line stays above the separator until the panel
 * closes — see `splitUnread` — so nothing moves under the cursor.
 *
 * ── WHOSE WORDS ARE WHOSE ─────────────────────────────────────────────────
 *
 * System titles follow the interface language. Authored titles and bodies stay
 * as stored. The severity is a CODE (`warning`), not authored text, so it is
 * translated like any label — printing "warning" raw on a Portuguese panel was
 * the bug of 27/09/2026.
 */
const open = ref(false);
const unreadOnly = ref(false);
const { items, total, unread, hasMore, loadingMore, loadMore } = useNotifications(unreadOnly);
// O fio que faz o crachá acender SEM F5: aqui e não no popover, porque o que a
// pessoa espera ver acender é o número no sino fechado — e o sino está montado
// no cabeçalho enquanto houver sessão. Ver `use-notification-stream.ts`.
useNotificationStream();
const markRead = useMarkRead();
const dismiss = useDismiss();
const router = useRouter();
const { t, when, mediumLabel, titleLabel, bodyLabel, severityLabel } = useNotificationsI18n();
// O rótulo do campo que a recusa de uma regra nomeia, pela tela do registro.
const entities = useEntities();
const fieldName = (item: Notification) => (code: string) =>
	entities.data.value?.find((e) => e.code === item.subjectCode)?.fields.find((f) => f.code === code)
		?.label;

// As não lidas vistas com o painel aberto. Um `reactive(Set)` preenchido pelo
// watch e esvaziado ao fechar: é o que deixa a linha clicada no lugar até a
// próxima abertura, em vez de ela saltar para "anteriores" quando o refetch volta.
const held = reactive(new Set<string>());
watch(
	[open, items],
	([isOpen, list]) => {
		if (!isOpen) return held.clear();
		for (const n of list) if (!n.readAt) held.add(n.id);
	},
	{ immediate: true },
);
const sections = computed(() => splitUnread(items.value, held));

function follow(item: Notification): void {
	const to = linkFor(item);
	if (!item.readAt) markRead.mutate({ ids: [item.id] });
	if (!to) return;
	open.value = false;
	void router.push(to);
}

const FILTER = "rounded-full px-2.5 py-0.5 text-2xs font-medium";
</script>

<template>
	<!-- `w-80` without the hand-written `max-w-[calc(100vw-2rem)]` that used to be
	     here: the panel leaves through a portal and clears the edge with 8px to
	     spare, which is what that arithmetic imitated — and imitated badly,
	     because it shrank the panel instead of moving it. -->
	<Popover
		:open="open"
		align="end"
		:label="t('bell.label')"
		panel-class="w-80 overflow-hidden"
		@update:open="open = $event"
	>
		<template #trigger>
			<button
				type="button"
				class="relative flex size-10 items-center justify-center rounded-full text-bar-fg transition-colors hover:bg-bar-hover hover:text-bar-fg-strong"
				:aria-label="t('bell.label')"
			>
				<!-- A bell, and not the `◔` that used to be here: the same glyph drew the
				     bell and the menu's "Usage" item, two subjects under one symbol.
				     Through `Icon` rather than a hand-written `<svg>`: it was the only
				     stroke on the top bar drawn at 1.5 while its neighbours were at 2.

				     36×36 e traço de 18px, nos tons `bar-*`: é a medida e a cor de
				     `.topbar-btn` na V2, a mesma que os outros botões desta barra
				     passaram a ter (ver `AppHeader.vue`). -->
				<Icon name="bell" class="size-[18px]" />
				<span
					v-if="unread"
					class="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-danger text-3xs font-medium text-fg-on-accent"
				>
					{{ unread > 9 ? "9+" : unread }}
				</span>
			</button>
		</template>

		<header class="border-b border-border/60 px-3 py-2">
			<div class="flex items-center justify-between gap-2">
				<p class="text-xs font-semibold text-fg">{{ t("bell.label") }}</p>
				<button
					v-if="unread"
					type="button"
					class="text-xs text-fg-subtle underline"
					@click="markRead.mutate({ all: true })"
				>
					{{ t("bell.markAllRead") }}
				</button>
			</div>
			<div class="mt-1.5 flex items-center gap-1">
				<button
					type="button"
					:class="[FILTER, unreadOnly ? 'text-fg-subtle hover:text-fg' : 'bg-bg-muted text-fg']"
					:aria-pressed="!unreadOnly"
					@click="unreadOnly = false"
				>
					{{ t("bell.all") }}
				</button>
				<button
					type="button"
					:class="[FILTER, unreadOnly ? 'bg-bg-muted text-fg' : 'text-fg-subtle hover:text-fg']"
					:aria-pressed="unreadOnly"
					@click="unreadOnly = true"
				>
					{{ t("bell.unread") }}{{ unread ? ` · ${unread}` : "" }}
				</button>
			</div>
		</header>

		<ul class="max-h-80 overflow-y-auto p-1">
			<li v-if="!items.length" class="px-3 py-6 text-center text-xs text-fg-subtle">
				{{ unreadOnly ? t("bell.nothingUnread") : t("bell.nothingHere") }}
			</li>
			<template v-for="(item, index) in [...sections.unread, ...sections.earlier]" :key="item.id">
			<!-- O separador só quando há as duas metades: sem não lidas, a lista é a
			     de sempre; com filtro "não lidas", não há "anteriores". -->
			<li
				v-if="index === sections.unread.length && index > 0"
				class="px-3 pb-1 pt-2 text-3xs font-medium uppercase tracking-wide text-fg-subtle"
				data-testid="bell-earlier"
			>
				{{ t("bell.earlier") }}
			</li>
			<li
				class="group flex items-start overflow-hidden rounded-lg"
				:class="item.readAt ? 'opacity-60' : ''"
			>
				<!-- A button and not an anchor: some notices have nowhere to go, and an
				     <a> without an href is a link that looks clickable and is not. -->
				<button
					type="button"
					class="min-w-0 flex-1 px-3 py-2.5 text-left"
					:class="linkFor(item) ? 'hover:bg-bg-muted' : 'cursor-default'"
					@click="follow(item)"
				>
					<div class="flex items-start justify-between gap-2">
						<p class="text-sm text-fg">{{ titleLabel(item) }}</p>
						<Badge
							v-if="item.severity !== 'info'"
							:tone="item.severity === 'urgent' ? 'danger' : 'warning'"
						>
							{{ severityLabel(item.severity) }}
						</Badge>
					</div>
					<p v-if="item.body" class="mt-0.5 text-xs text-fg-muted">{{ bodyLabel(item, fieldName(item)) }}</p>
					<p class="mt-1 flex items-center gap-2 text-2xs text-fg-subtle">
						<span>{{ when(item.createdAt) }}</span>
						<!-- Said out loud, because "I never got it on WhatsApp" is answered by
						     this line and not by a database. -->
						<span v-if="item.medium !== 'bell'">
							{{ t("bell.alsoBy", { medium: mediumLabel(item.medium) }) }}
						</span>
						<span v-if="linkFor(item)" class="text-accent">{{ t("bell.open") }}</span>
					</p>
				</button>
				<button
					v-if="dismissible(item)"
					type="button"
					class="shrink-0 self-stretch px-2 text-sm text-fg-subtle opacity-0 group-hover:opacity-100 hover:text-danger focus-visible:opacity-100 pointer-coarse:opacity-100"
					:aria-label="t('bell.dismiss', { title: titleLabel(item) })"
					@click="dismiss.mutate([item.id])"
				>
					×
				</button>
			</li>
			</template>
		</ul>

		<div v-if="hasMore" class="border-t border-border/60 p-1.5">
			<button
				type="button"
				class="w-full rounded-full py-1.5 text-2xs text-fg-subtle hover:bg-bg-muted hover:text-fg"
				:disabled="loadingMore"
				@click="loadMore()"
			>
				{{
					loadingMore
						? t("bell.loadingMore")
						: t("bell.loadMore", { shown: items.length, total })
				}}
			</button>
		</div>
	</Popover>
</template>
